<template>
  <ComboboxRoot
    v-model="selectedProjectIds"
    v-model:open="isOpen"
    :open-on-focus="true"
    :ignore-filter="true"
    :multiple="true"
    @update:model-value="handleSelectionChange"
  >
    <ComboboxAnchor :class="$style.comboboxAnchor">
      <ComboboxInput
        :class="$style.comboboxInput"
        v-model="searchInput"
        placeholder="Search projects..."
      />
      <ComboboxTrigger :class="$style.comboboxTrigger"> ▼ </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxContent
      position="popper"
      :class="$style.comboboxContent"
      @pointer-down-outside="handleClickOutside"
    >
      <ComboboxViewport :class="$style.comboboxViewport">
        <ComboboxItem
          v-for="project in filteredProjects"
          :key="project.id"
          :value="project.id"
          :class="$style.comboboxItem"
        >
          <ComboboxItemIndicator :class="$style.itemIndicator">
            ✓
          </ComboboxItemIndicator>
          {{ project.name }}
        </ComboboxItem>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>

  <div :class="$style.selectedChips">
    <Chip
      v-for="projectId in selectedProjectIds"
      :key="projectId"
      :label="getProjectName(projectId)"
      :show-clear="true"
      @clear="removeProject(projectId)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import {
  ComboboxRoot,
  ComboboxAnchor,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxViewport,
  ComboboxItem,
  ComboboxItemIndicator,
} from "reka-ui";
import { projectService } from "../services/projectService";
import Chip from "./Chip.vue";

const props = defineProps<{
  modelValue?: string[];
  usePortal?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
}>();

const selectedProjectIds = ref<string[]>(props.modelValue || []);
const searchInput = ref("");
const isOpen = ref(false);

const availableProjects = computed(() => projectService.getProjects());

function handleClickOutside() {
  // Close the dropdown when clicking outside
  // The @pointer-down-outside event only fires when Reka UI detects a click outside
  // Since we're controlling the open state with v-model:open, we need to close manually
  isOpen.value = false;
}

const filteredProjects = computed(() => {
  if (!searchInput.value.trim()) {
    return availableProjects.value;
  }
  const searchTerm = searchInput.value.toLowerCase().trim();
  return availableProjects.value.filter((project) =>
    project.name.toLowerCase().includes(searchTerm)
  );
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== undefined) {
      selectedProjectIds.value = [...newValue];
    }
  }
);

function handleSelectionChange(value: string | string[]) {
  const newSelection = Array.isArray(value) ? value : [];
  selectedProjectIds.value = newSelection;
  emit("update:modelValue", [...newSelection]);
}

function removeProject(projectId: string) {
  const newSelection = selectedProjectIds.value.filter(
    (id) => id !== projectId
  );
  selectedProjectIds.value = newSelection;
  emit("update:modelValue", [...newSelection]);
}

function getProjectName(projectId: string): string {
  const project = availableProjects.value.find((p) => p.id === projectId);
  return project?.name || projectId;
}
</script>

<style module>
.projectMultiSelect {
  position: relative;
  min-width: 200px;
}

.comboboxAnchor {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  min-width: 200px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  padding: 0;
  gap: 5px;
  transition: all 0.2s ease;
}

.comboboxAnchor:focus-within {
  border-color: var(--primary-color);
  outline: 1px solid var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.comboboxInput {
  flex: 1;
  background: transparent;
  border: none;
  padding: 8px 12px;
  color: white;
  font-size: 16px;
  font-family: inherit;
  outline: none;
}

.comboboxInput::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.comboboxTrigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  border: none;
  background: transparent;
  font-size: 0.8rem;
  transition: color 0.2s ease;
}

.comboboxTrigger:hover {
  color: white;
}

.comboboxContent {
  background: #2c3e50;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  min-width: 200px;
  overflow: hidden;
}

.comboboxViewport {
  padding: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.comboboxItem {
  padding: 8px 12px 8px 30px;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9em;
  user-select: none;
  position: relative;
  display: flex;
  align-items: center;
}

.comboboxItem:hover,
.comboboxItem[data-highlighted] {
  background: rgba(255, 255, 255, 0.2);
  outline: none;
}

.comboboxItem[data-selected] {
  background: var(--primary-color);
  color: white;
}

.itemIndicator {
  position: absolute;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  font-size: 0.8em;
}

.selectedChips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  min-height: 2rem;
}
</style>
